import { useImageAnnotatorContext } from "@msuite/image-annotator";
import type { PolygonAnnotation as PolygonAnnotationType } from "@msuite/utils/types";
import { Fragment, useId } from "react";
import { AnnotationDragHandle } from "./annotation-drag-handle";
import { AnnotationText } from "./annotation-text";
import { getPatternId, Pattern } from "./patterns";

/** Props Interface */
interface PolygonAnnotationProps {
	annotation: PolygonAnnotationType;
}

export function PolygonAnnotation({ annotation }: PolygonAnnotationProps) {
	/** Context */
	const context = useImageAnnotatorContext();

	/** Hooks */
	const salt = useId();

	/** Functions */
	function handleOnClick(e: React.MouseEvent) {
		if (context.controller.isReadOnly) return;
		/** While a creation tool is armed the click belongs to the canvas, not to selection. */
		if (context.controller.tool !== "select") return;
		e.stopPropagation();
		context.controller.setSelectionId(annotation.id);
	}

	/** Computed */
	const isSelected = context.controller.selectionId === annotation.id;
	const cx = annotation.points.reduce((s, p) => s + p.x, 0) / annotation.points.length;
	const cy = annotation.points.reduce((s, p) => s + p.y, 0) / annotation.points.length;

	/** Render */
	return (
		<g onClick={handleOnClick}>
			<Pattern
				type={annotation.fillPattern}
				color={annotation.fillColor}
				salt={salt}
			/>
			<polygon
				points={annotation.points.map((p) => `${p.x},${p.y}`).join(" ")}
				stroke={annotation.strokeColor}
				strokeWidth={annotation.strokeWidth ? (annotation.strokeWidth || 3) / context.baseScale : 0}
				fill={getPatternId(annotation.fillPattern, salt)}
				style={{ cursor: context.controller.isReadOnly ? undefined : "pointer" }}
			/>
			{isSelected && !context.controller.isReadOnly && (
				<Fragment>
					{annotation.points.map((point, index) => (
						<AnnotationDragHandle
							key={index}
							cx={point.x}
							cy={point.y}
							annotationId={annotation.id}
							kind="poly"
							index={index}
						/>
					))}
				</Fragment>
			)}
			{annotation.text && (
				<AnnotationText
					cx={cx}
					cy={cy}
					text={annotation.text}
				/>
			)}
		</g>
	);
}
